import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Form, Input, Button, Row, Col, InputNumber, Select, DatePicker, Card, message } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useSale } from '../../store/saleContext';
import { useProduct } from '../../store/productContext';
import { useCustomer } from '../../store/customerContext';
import { useCurrency } from '../../store/currencyContext';
import dayjs from 'dayjs';
import { SellInvoice } from '../../types/sale';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import PrintableInvoice from '../Print/PrintableInvoice';
import ItemStockHint from '../Product/ItemStockHint';

const UNIT_VALUES = ["piece", "set", "carton", "meter", "roll", "box", "other"];

// Type aliases for formatters
type FormatterValue = number | string | undefined;
type ParserValue = string | undefined;

interface SaleFormProps {
    editSale?: SellInvoice;
    onFinishEdit?: () => void;
}

const SaleForm: React.FC<SaleFormProps> = ({ editSale, onFinishEdit }) => {
    const [form] = Form.useForm();
    const { addSale, updateSale } = useSale();
    const { customers, fetchCustomers } = useCustomer();
    const { products, fetchProducts } = useProduct();
    const { currencies } = useCurrency();
    const [currencyDisabled, setCurrencyDisabled] = useState(false);
    const [newInvoice, setNewInvoice] = useState<SellInvoice | null>(null);
    const printRef = useRef<HTMLDivElement>(null);
    const editSaleIdRef = useRef<string | undefined>(undefined);
    const { t } = useTranslation();

    // Translated units
    const UNITS = UNIT_VALUES.map(unit => ({ value: unit, label: t(`unit.${unit}`) }));

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: t('sale_invoice') || 'Sale Invoice',
    });

    const DEFAULT_CURRENCY = useMemo(() => currencies.find(c => c.isDefault)?.code || "USD", [currencies]);
    const currencyList = useMemo(() => currencies.map(c => ({
        value: c.code,
        label: c.symbol ? `${c.code} (${c.symbol})` : c.code
    })), [currencies]);

    const isItemsFilled = useCallback(() => {
        const items = form.getFieldValue('items') || [];
        return items.some((item: any) =>
            item && (item.product || item.qty || item.sellPrice)
        );
    }, [form]);

    useEffect(() => {
        // Only fetch data once when component mounts
        fetchCustomers();
        fetchProducts();
    }, []);

    useEffect(() => {
        if (editSale && customers.length > 0 && products.length > 0) {
            // Prevent re-initializing the form when customers/products are re-fetched
            // while the user is actively editing (which would reset their changes)
            if (editSaleIdRef.current === editSale.id) return;
            editSaleIdRef.current = editSale.id;

            let customerValue: any = undefined;
            let customerPhone = "";
            let customerName = "";
            if (editSale.customer && typeof editSale.customer === 'object') {
                const cid = (editSale.customer as any).id || (editSale.customer as any)._id;
                if (cid) {
                    customerValue = cid;
                } else {
                    // True direct sale — customer has no database ID; leave Select empty
                    customerPhone = (editSale.customer as any)?.phone || "";
                    customerName = (editSale.customer as any)?.name || "";
                    customerValue = undefined;
                }
            } else if (typeof editSale.customer === 'string') {
                customerValue = editSale.customer;
            }


            // Handle items data - if product is an object, extract the ID
            let itemsValue = editSale.items?.map(item => {
                let productValue = item.product;
                if (item.product && typeof item.product === 'object') {
                    productValue = (item.product as any).id || (item.product as any)._id;
                }
                return {
                    ...item,
                    product: productValue
                };
            }) || [];
            form.setFieldsValue({
                ...editSale,
                customer: customerValue,
                customerName: customerName,
                customerPhone: customerPhone,
                items: itemsValue,
                date: editSale.date ? dayjs(editSale.date) : dayjs(),
            });
        } else if (!editSale) {
            editSaleIdRef.current = undefined;
            form.setFieldsValue({
                date: dayjs(),
                items: [{}],
                paid: 0,
                remain: 0,
                total: 0,
                discount: 0,
                currency: DEFAULT_CURRENCY,
            });
        }
        setCurrencyDisabled(isItemsFilled());
    }, [editSale, customers, products, DEFAULT_CURRENCY, form, isItemsFilled]);



    const getCustomerName = (customer?: any, customerName?: string, customersList = customers): string => {
        if (customer) {
            return customer?.name || "-";
        }
        return customerName || "-";
    };

    const getProductName = (product?: any, productList = products): string => {
        return product?.name || "-";
    };

    const customerOptions = useMemo(() => {
        const options = customers.map(c => ({
            value: c.id,
            label: c.name,
        }));
        return options;
    }, [customers]);

    const productOptions = useMemo(() => {
        const options = products.map(p => ({
            value: p.id,
            label: p.name,
        }));
        return options;
    }, [products]); const handleCustomerChange = (value: any) => {
        form.setFieldsValue({
            customerName: undefined,
            customerPhone: undefined,
            customer: value,
        });
    };

    const handleCustomerNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value) {
            form.setFieldsValue({ customer: undefined });
        }
    };

    const handleItemChange = (changed: any, all: any) => {
        const items = Array.isArray(all.items) ? all.items : [];
        const subtotal = items.reduce((sum: number, item: any) => {
            if (item?.qty && item?.sellPrice) return sum + (Number(item.qty) * Number(item.sellPrice));
            return sum;
        }, 0);
        // Whole-invoice discount (fixed amount), capped to [0, subtotal] so the net total
        // never goes below 0 and the invariant `subtotal = total + discount` always holds.
        const rawDiscount = Number(form.getFieldValue('discount')) || 0;
        const discount = Math.min(Math.max(rawDiscount, 0), subtotal);
        if (rawDiscount !== discount) {
            form.setFieldsValue({ discount });
        }
        const total = subtotal - discount;
        if (form.getFieldValue('total') !== total) {
            form.setFieldsValue({ total });
        }
        const paid = form.getFieldValue('paid') || 0;
        setCurrencyDisabled(isItemsFilled());
        form.setFieldsValue({ remain: Math.max(total - paid, 0) });
    };

    const handleNewInvoice = () => {
        form.resetFields();
        form.setFieldsValue({
            date: dayjs(),
            items: [{}],
            paid: 0,
            remain: 0,
            total: 0,
            discount: 0,
            currency: DEFAULT_CURRENCY,
        });
        setCurrencyDisabled(false);
        setNewInvoice(null)
    };

    const handleProductSelect = (productId: string, idx: number) => {
        const invoiceCurrency = form.getFieldValue('currency') || DEFAULT_CURRENCY;
        const prod = products.find(p => p.id === productId);
        let price = 0;
        if (prod && Array.isArray(prod.currentStock)) {
            if (invoiceCurrency === "USD") {
                price = prod.sellPriceInDollar;
            } else if (invoiceCurrency === "IQD") {
                price = prod.sellPriceInDinar;
            }
        }
        const items = form.getFieldValue('items') || [];
        items[idx] = { ...items[idx], unit: "piece", qty: 1, sellPrice: price };
        form.setFieldsValue({ items });
        handleItemChange({}, { items });
    };

    const focusOnNewProductInput = (idx: number) => {
        setTimeout(() => {
            const el = document.getElementById(`item-${idx}`);
            if (el) el.focus();
        }, 80);
    };

    const formatNumber = (value: any) =>
        value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";

    const onFinish = async (values: any) => {

        if (!values.items || values.items.length === 0 || values.items.some((item: any) => !item.product || !item.qty || !item.sellPrice)) {
            message.error(t('at_least_one_sale_item') || 'At least one sale item is required');
            return;
        }
        const items = values.items.map((item: any) => ({
            product: item.product,
            unit: item.unit,
            qty: Number(item.qty),
            unitLength: item.unitLength ? Number(item.unitLength) : undefined,
            sellPrice: Number(item.sellPrice),
            discount: item.discount ? Number(item.discount) : undefined,
            attributes: item.attributes || undefined,
        }));

        let payload: any = {
            ...values,
            date: values.date ? values.date.toISOString() : undefined,
            items,
            discount: values.discount ? Number(values.discount) : 0,
            customer: values.customer || undefined,
            warehouse: values.warehouse || undefined,
        };

        // For database storage, remove redundant fields
        let dbPayload = { ...payload };
        if (dbPayload.customer) {
            // Explicitly set to undefined so they override any old values from editSale when spreading
            dbPayload.customerName = undefined;
            dbPayload.customerPhone = undefined;
        } else {
            delete dbPayload.customer;
        }


        try {
            if (editSale) {
                await updateSale({ ...editSale, ...dbPayload });
                // Allow the form to re-initialize from the fresh server data
                editSaleIdRef.current = undefined;
                message.success(t('sale_updated') || 'Sale updated successfully');
                if (onFinishEdit) onFinishEdit();
            } else {
                const createdSale = await addSale(dbPayload);
                const printInvoice = {
                    ...createdSale,
                    customerName: (createdSale.customer as any)?.name || "-",
                    customerPhone: (createdSale.customer as any)?.phone || "-",
                    customer: createdSale.customer
                };
                setNewInvoice(printInvoice);
                message.success(t('sale_created') || 'Sale created successfully');
                setTimeout(() => {
                    handlePrint();
                }, 500);
            }
        } catch (err: any) {
            message.error(err.message || t('something_went_wrong') || 'Something went wrong');
        }
    };

    return (
        <div>
            <Row justify="end" style={{ marginBottom: 8 }}>
                <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={handleNewInvoice}
                >
                    {t('new_invoice')}
                </Button>
            </Row>
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                style={{
                    maxWidth: 1400,
                    minWidth: 480,
                    margin: '0 auto',
                    padding: 16,
                    background: "#fff",
                    borderRadius: 18,
                    boxShadow: '0 2px 16px rgba(39,21,107,0.06)'
                }}
                onValuesChange={handleItemChange}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col xs={24} md={8}>
                        <Form.Item
                            name="customer"
                            label={t('customer')}
                            dependencies={['customerName']}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const customerName = getFieldValue('customerName');
                                        if (value || customerName) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t('select_customer_or_enter_customer_name') || 'Please select customer or enter name'));
                                    }
                                })
                            ]}
                        >
                            <Select
                                options={customerOptions}
                                placeholder={t('select_customer') || 'Select customer'}
                                showSearch
                                optionFilterProp="label"
                                allowClear
                                onChange={handleCustomerChange}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            name="customerName"
                            label={t('customer_name')}
                            dependencies={['customer']}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const customer = getFieldValue('customer');
                                        if (value || customer) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t('select_customer_or_enter_customer_name') || 'Please select customer or enter name'));
                                    }
                                })
                            ]}
                        >
                            <Input placeholder={t('direct_sale_name') || 'Customer name'} onChange={handleCustomerNameChange} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item name="customerPhone" label={t('customer_phone')}>
                            <Input placeholder={t('direct_sale_phone') || 'Customer phone'} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} md={8}>
                        <Form.Item name="currency" label={t('currency')} rules={[{ required: true }]}>
                            <Select disabled={currencyDisabled} options={currencyList} placeholder={t('currency') || 'Currency'} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item name="date" label={t('date')} rules={[{ required: true }]}>
                            <DatePicker style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Card title={t('sale_items')} style={{ marginBottom: 16, borderRadius: 12 }}>
                    <Form.List
                        name="items"
                        rules={[
                            {
                                validator: async (_, items) => {
                                    if (!items || items.length < 1 || items.every((item: any) => !item.product)) {
                                        return Promise.reject(new Error(t('at_least_one_sale_item') || 'At least one sale item is required'));
                                    }
                                }
                            }
                        ]}
                    >
                        {(fields, { add, remove }, { errors }) => (
                            <>
                                <Row gutter={8} style={{ fontWeight: 600, marginBottom: 4 }}>
                                    <Col span={5}>{t('product')}</Col>
                                    <Col span={3}>{t('unit')}</Col>
                                    <Col span={3}>{t('qty')}</Col>
                                    <Col span={4}>{t('sell_price')}</Col>
                                    <Col span={3}>{t('length')}</Col>
                                    <Col span={3}>{t('total')}</Col>
                                    <Col span={3}>X</Col>
                                </Row>
                                {fields.map(({ key, name, ...restField }, idx) => {
                                    const unit = form.getFieldValue(['items', name, 'unit']);
                                    const showUnitLength = unit === "roll" || unit === "set" || unit === "carton" || unit === "box";
                                    return (
                                        <Row key={key} gutter={8} style={{ marginBottom: 6 }}>
                                            <Col span={5}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'product']}
                                                    rules={[{ required: true, message: t('select_product') || 'Please select product' }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Select
                                                        options={productOptions}
                                                        placeholder={t('product') || 'Product'}
                                                        showSearch
                                                        optionFilterProp="label"
                                                        onChange={(val) => handleProductSelect(val, idx)}
                                                        onKeyDown={e => {
                                                            if (e.key === "Enter") {
                                                                e.preventDefault();
                                                                focusOnNewProductInput(idx + 1);
                                                                add();
                                                            }
                                                        }}
                                                        id={`item-${idx}`}
                                                    />
                                                </Form.Item>
                                                <ItemStockHint form={form} name={name} products={products} type="sale" />
                                            </Col>
                                            <Col span={3}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'unit']}
                                                    rules={[{ required: true, message: t('select_unit') || 'Please select unit' }]}
                                                    style={{ marginBottom: 0 }}
                                                    initialValue={UNITS[0].value}
                                                >
                                                    <Select
                                                        options={UNITS}
                                                        placeholder={t('unit') || 'Unit'}
                                                        onChange={() => form.validateFields()} // rerender for dynamic field
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={3}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'qty']}
                                                    rules={[{ required: true, message: t('enter_qty') || 'Please enter quantity' }]}
                                                    style={{ marginBottom: 0 }}
                                                    initialValue={1}
                                                >
                                                    <InputNumber min={1} placeholder={t('qty') || 'Quantity'} style={{ width: "100%" }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={4}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'sellPrice']}
                                                    rules={[{ required: true, message: t('enter_sell_price') || 'Please enter sell price' }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <InputNumber
                                                        formatter={(value: FormatterValue) =>
                                                            value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                                                        }
                                                        parser={(value: ParserValue) =>
                                                            value ? value.replace(/,/g, "") : ""
                                                        }
                                                        min={0}
                                                        placeholder={t('sell_price') || 'Sell price'}
                                                        style={{ width: "100%" }}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            {/* Dynamic Length Field */}
                                            <Col span={3}>
                                                {showUnitLength && (
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'unitLength']}
                                                        rules={[{ required: true, message: t('enter_length') || 'Please enter length' }]}
                                                        style={{ marginBottom: 0 }}
                                                    >
                                                        <InputNumber
                                                            formatter={(value: FormatterValue) =>
                                                                value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                                                            }
                                                            parser={(value: ParserValue) =>
                                                                value ? value.replace(/,/g, "") : ""
                                                            }
                                                            min={0}
                                                            placeholder={t('length') || 'Length'}
                                                            style={{ width: "100%" }}
                                                        />
                                                    </Form.Item>
                                                )}
                                            </Col>
                                            <Col span={3}>
                                                <Form.Item shouldUpdate>
                                                    {() => {
                                                        const qty = form.getFieldValue(['items', name, 'qty']) || 0;
                                                        const sellPrice = form.getFieldValue(['items', name, 'sellPrice']) || 0;
                                                        const total = Number(qty) * Number(sellPrice);
                                                        return (
                                                            <InputNumber
                                                                value={isNaN(total) ? 0 : total}
                                                                style={{ width: "100%" }}
                                                                readOnly
                                                                disabled
                                                                formatter={formatNumber}
                                                            />
                                                        );
                                                    }}
                                                </Form.Item>
                                            </Col>
                                            <Col span={3} style={{ display: "flex", alignItems: "center" }}>
                                                <Button
                                                    type="link"
                                                    danger
                                                    icon={<MinusCircleOutlined />}
                                                    onClick={() => remove(name)}
                                                    tabIndex={-1}
                                                />
                                            </Col>
                                        </Row>
                                    );
                                })}
                                <Form.ErrorList errors={errors} />
                                <Button
                                    type="dashed"
                                    block
                                    icon={<PlusOutlined />}
                                    onClick={() => {
                                        add();
                                        focusOnNewProductInput(fields.length);
                                    }}
                                    style={{ marginTop: 4 }}
                                >
                                    {t('add_item')}
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Card>
                <Row gutter={16}>
                    <Col xs={24} md={8}>
                        <Form.Item label={t('subtotal')} shouldUpdate>
                            {() => {
                                const items = form.getFieldValue('items') || [];
                                const subtotal = items.reduce((sum: number, item: any) =>
                                    (item?.qty && item?.sellPrice)
                                        ? sum + Number(item.qty) * Number(item.sellPrice)
                                        : sum, 0);
                                return (
                                    <InputNumber
                                        value={subtotal}
                                        formatter={formatNumber}
                                        min={0}
                                        style={{ width: "100%" }}
                                        readOnly
                                        disabled
                                    />
                                );
                            }}
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item name="discount" label={t('discount')} initialValue={0}>
                            <InputNumber
                                formatter={(value: FormatterValue) =>
                                    value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                                }
                                parser={(value: ParserValue) =>
                                    value ? value.replace(/,/g, "") : ""
                                }
                                min={0}
                                style={{ width: "100%" }}
                                placeholder={t('discount') || 'Discount'}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item name="total" label={t('total')} rules={[{ required: true }]}>
                            <InputNumber
                                formatter={(value: FormatterValue) =>
                                    value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                                }
                                parser={(value: ParserValue) =>
                                    value ? value.replace(/,/g, "") : ""
                                } min={0} style={{ width: "100%" }} readOnly />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item name="paid" label={t('paid')} rules={[{ required: true }]}>
                            <InputNumber formatter={(value: FormatterValue) =>
                                value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                            }
                                parser={(value: ParserValue) =>
                                    value ? value.replace(/,/g, "") : ""
                                } min={0} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item name="remain" label={t('remain')} rules={[{ required: true }]}>
                            <InputNumber formatter={(value: FormatterValue) =>
                                value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                            }
                                parser={(value: ParserValue) =>
                                    value ? value.replace(/,/g, "") : ""
                                } min={0} style={{ width: "100%" }} readOnly />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item name="note" label={t('note')}>
                            <Input.TextArea placeholder={t('note') || 'Note'} autoSize />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item>
                    {newInvoice ? (
                        <>
                            <div style={{ display: "none" }}>
                                <PrintableInvoice
                                    ref={printRef}
                                    type="sale"
                                    invoice={newInvoice}
                                    getCustomerOrSupplierName={() => {
                                        const customerName = getCustomerName(newInvoice.customer, newInvoice.customerName, customers);
                                        return customerName;
                                    }}
                                    getProductName={(id) => {
                                        const productName = getProductName(id, products);
                                        return productName;
                                    }}
                                />
                            </div>
                            <Row gutter={12}>
                                <Col xs={24} md={12}>
                                    <Button
                                        type="default"
                                        block
                                        onClick={handlePrint}
                                    >
                                        🖨️ {t('print_invoice')}
                                    </Button>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Button
                                        type="primary"
                                        block
                                        onClick={handleNewInvoice}
                                    >
                                        ➕ {t('new_invoice')}
                                    </Button>
                                </Col>
                            </Row>
                        </>
                    ) : (
                        <Button type="primary" htmlType="submit" block size="large">
                            {editSale ? t('update_sale') : `➕ ${t('create_sale')}`}
                        </Button>
                    )}
                </Form.Item>
            </Form>
        </div>
    );
};

export default SaleForm;
